import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import { crisisData, prevalenceTrend, spendingPayer, edReadmit, roiData, workforceData, outcomesData, costOfCareData } from "./data";

const NAVY = "#0a1a3c";
const TEAL = "#15a8c2";
const GOLD = "#c7a14a";
const GRID = "#e3e9f2";
const AXIS = { fontSize: 11, fontFamily: "Libre Franklin", fill: "#46566c" };
const tip = { borderRadius: 10, border: "1px solid #e3e9f2", fontFamily: "Libre Franklin", fontSize: 12 };

export const CrisisChart = () => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={crisisData} layout="vertical" margin={{ left: 10, right: 30 }}>
      <CartesianGrid horizontal={false} stroke={GRID} />
      <XAxis type="number" tick={AXIS} unit="M" />
      <YAxis type="category" dataKey="label" tick={AXIS} width={170} />
      <Tooltip contentStyle={tip} formatter={(v) => [`${v} million`, "U.S. adults"]} />
      <Bar dataKey="value" fill={TEAL} radius={[0, 6, 6, 0]} barSize={26} />
    </BarChart>
  </ResponsiveContainer>
);

export const PrevalenceTrend = () => (
  <ResponsiveContainer width="100%" height={250}>
    <AreaChart data={prevalenceTrend} margin={{ left: -10, right: 10 }}>
      <defs>
        <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={TEAL} stopOpacity={0.5} /><stop offset="100%" stopColor={TEAL} stopOpacity={0} /></linearGradient>
        <linearGradient id="gY" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={GOLD} stopOpacity={0.5} /><stop offset="100%" stopColor={GOLD} stopOpacity={0} /></linearGradient>
      </defs>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="year" tick={AXIS} />
      <YAxis tick={AXIS} unit="M" />
      <Tooltip contentStyle={tip} />
      <Legend wrapperStyle={{ fontFamily: "Libre Franklin", fontSize: 12 }} />
      <Area type="monotone" name="Adults (M)" dataKey="adults" stroke={TEAL} strokeWidth={2.5} fill="url(#gA)" />
      <Area type="monotone" name="Youth 12–17 (%)" dataKey="youth" stroke={GOLD} strokeWidth={2.5} fill="url(#gY)" />
    </AreaChart>
  </ResponsiveContainer>
);

const PIE_COLORS = [TEAL, NAVY, "#1b3568", GOLD, "#6b7a90", "#bfe6ee"];
export const SpendingChart = () => (
  <ResponsiveContainer width="100%" height={260}>
    <PieChart>
      <Pie data={spendingPayer} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}
        label={({ name, value }) => `${name} ${value}%`} labelLine={false} style={{ fontFamily: "Libre Franklin", fontSize: 11 }}>
        {spendingPayer.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
      </Pie>
      <Tooltip contentStyle={tip} formatter={(v) => [`${v}% of national BH spending`, ""]} />
    </PieChart>
  </ResponsiveContainer>
);

export const EDReadmitChart = () => (
  <ResponsiveContainer width="100%" height={280}>
    <BarChart data={edReadmit} margin={{ left: -8, right: 10 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="metric" tick={{ ...AXIS, fontSize: 10 }} interval={0} />
      <YAxis tick={AXIS} unit="" />
      <Tooltip contentStyle={tip} formatter={(v, n) => [`${v} (indexed)`, n === "baseline" ? "Baseline" : "Projected w/ GH"]} />
      <Legend wrapperStyle={{ fontFamily: "Libre Franklin", fontSize: 12 }} />
      <Bar name="Baseline (index = 100)" dataKey="baseline" fill={NAVY} radius={[5, 5, 0, 0]} barSize={26} />
      <Bar name="Projected with GH" dataKey="projected" fill={TEAL} radius={[5, 5, 0, 0]} barSize={26} />
    </BarChart>
  </ResponsiveContainer>
);

export const ROIChart = () => (
  <ResponsiveContainer width="100%" height={290}>
    <LineChart data={roiData} margin={{ left: 0, right: 14 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="month" tick={AXIS} />
      <YAxis tick={AXIS} unit="K" />
      <Tooltip contentStyle={tip} formatter={(v, n) => [`$${v}K`, n === "savings" ? "Cumulative gross savings" : n === "cost" ? "Cumulative program cost" : "Net position"]} />
      <Legend wrapperStyle={{ fontFamily: "Libre Franklin", fontSize: 12 }} />
      <Line name="Cumulative savings" dataKey="savings" stroke={TEAL} strokeWidth={3} dot={false} />
      <Line name="Program cost" dataKey="cost" stroke="#6b7a90" strokeWidth={2} strokeDasharray="5 4" dot={false} />
      <Line name="Net position" dataKey="net" stroke={GOLD} strokeWidth={3} dot={{ r: 2 }} />
    </LineChart>
  </ResponsiveContainer>
);

export const WorkforceChart = () => (
  <ResponsiveContainer width="100%" height={270}>
    <BarChart data={workforceData} layout="vertical" margin={{ left: 20, right: 30 }}>
      <CartesianGrid horizontal={false} stroke={GRID} />
      <XAxis type="number" tick={AXIS} unit="%" domain={[0, 100]} />
      <YAxis type="category" dataKey="role" tick={AXIS} width={120} />
      <Tooltip contentStyle={tip} formatter={(v, n) => [`${v}%`, n === "supply" ? "Current effective supply" : "Estimated demand"]} />
      <Legend wrapperStyle={{ fontFamily: "Libre Franklin", fontSize: 12 }} />
      <Bar name="Demand" dataKey="demand" fill="#dce6f2" radius={[0, 5, 5, 0]} barSize={18} />
      <Bar name="Effective supply" dataKey="supply" fill={TEAL} radius={[0, 5, 5, 0]} barSize={18} />
    </BarChart>
  </ResponsiveContainer>
);

export const OutcomesRadar = () => (
  <ResponsiveContainer width="100%" height={290}>
    <RadarChart data={outcomesData} outerRadius={100}>
      <PolarGrid stroke={GRID} />
      <PolarAngleAxis dataKey="measure" tick={{ ...AXIS, fontSize: 11 }} />
      <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#9fb0c9" }} />
      <Radar name="Projected target (%)" dataKey="value" stroke={TEAL} fill={TEAL} fillOpacity={0.35} strokeWidth={2} />
      <Tooltip contentStyle={tip} formatter={(v) => [`${v}%`, "Projected target"]} />
    </RadarChart>
  </ResponsiveContainer>
);

export const CostOfCareChart = () => (
  <ResponsiveContainer width="100%" height={250}>
    <BarChart data={costOfCareData} margin={{ left: 10, right: 10 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="cohort" tick={AXIS} />
      <YAxis tick={AXIS} unit="$" />
      <Tooltip contentStyle={tip} formatter={(v) => [`$${v} PMPM`, "Total cost of care"]} />
      <Bar dataKey="pmpm" radius={[6, 6, 0, 0]} barSize={70}>
        {costOfCareData.map((e, i) => <Cell key={i} fill={i === 2 ? TEAL : i === 1 ? "#1b3568" : NAVY} />)}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);
